import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { extractErrorMessage } from '@core/http/api-response.envelope';
import { ToastService } from '@app/shared/components/toast/services/toast.service';

import { AuthService } from '../../../services/auth.service';
import {
  AccountActivationResponse,
  VerifyOtpResponse,
  CompletePasswordRecoveryResponse,
} from '../../../models/auth.model';
import {
  submitUsername,
  submitUsernameSuccess,
  submitUsernameFailure,
  verifyOtp,
  verifyOtpSuccess,
  verifyOtpFailure,
  resendOtp,
  resendOtpSuccess,
  resendOtpFailure,
  setPassword,
  setPasswordSuccess,
  setPasswordFailure,
  complete,
  setError,
} from './password-recovery.actions';
import {
  selectActivationToken,
  selectUsername,
} from './password-recovery.selectors';

@Injectable()
export class PasswordRecoveryEffects {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  submitUsername$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitUsername),
      switchMap(({ username }) =>
        this.authService.initiatePasswordRecovery(username).pipe(
          map((res: AccountActivationResponse) => {
            const data = res?.data;
            return submitUsernameSuccess({
              username,
              activationToken: data?.token ?? null,
              tokenType: data?.tokenType ?? null,
              nextStep: data?.nextStep ?? null,
              loginMethod: data?.loginMethod ?? null,
              expiresIn: data?.expiresIn ?? null,
              expiresAt: data?.expiresAt ?? null,
              message: data?.message ?? null,
            });
          }),
          catchError((err: any) =>
            of(submitUsernameFailure({ error: this.normalizeApiError(err) }))
          )
        )
      )
    )
  );

  submitUsernameSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitUsernameSuccess),
        map(() => {
          this.router.navigate(['/auth/password-recovery/verify-otp']);
        })
      ),
    { dispatch: false }
  );

  verifyOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyOtp),
      withLatestFrom(this.store.select(selectActivationToken)),
      switchMap(([{ code }, activationToken]) => {
        if (!activationToken) {
          return of(
            setError({
              error: this.translate.instant(
                'auth.passwordRecovery.errors.missingRecoveryCode'
              ),
            })
          );
        }
        return this.authService
          .verifyOtp({ otpCode: code, activationToken })
          .pipe(
            map((res: VerifyOtpResponse) => {
              const data = res?.data;
              return verifyOtpSuccess({
                activationToken: data?.token ?? activationToken,
                tokenType: data?.tokenType ?? null,
                nextStep: data?.nextStep ?? null,
                loginMethod: data?.loginMethod ?? null,
                expiresIn: data?.expiresIn ?? null,
                expiresAt: data?.expiresAt ?? null,
                message: data?.message ?? null,
              });
            }),
            catchError((err: any) =>
              of(verifyOtpFailure({ error: this.normalizeApiError(err) }))
            )
          );
      })
    )
  );

  verifyOtpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(verifyOtpSuccess),
        map(() => {
          this.router.navigate(['/auth/password-recovery/set-password']);
        })
      ),
    { dispatch: false }
  );

  resendOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resendOtp),
      withLatestFrom(this.store.select(selectUsername)),
      switchMap(([, username]) => {
        if (!username) {
          return of(
            setError({
              error: this.translate.instant(
                'auth.passwordRecovery.errors.missingUsername'
              ),
            })
          );
        }
        return this.authService.initiatePasswordRecovery(username).pipe(
          map((res: AccountActivationResponse) => {
            const data = res?.data;
            return resendOtpSuccess({
              activationToken: data?.token ?? null,
              tokenType: data?.tokenType ?? null,
              nextStep: data?.nextStep ?? null,
              loginMethod: data?.loginMethod ?? null,
              expiresIn: data?.expiresIn ?? null,
              expiresAt: data?.expiresAt ?? null,
              message: data?.message ?? null,
            });
          }),
          catchError((err: any) =>
            of(resendOtpFailure({ error: this.normalizeApiError(err) }))
          )
        );
      })
    )
  );

  setPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPassword),
      withLatestFrom(this.store.select(selectActivationToken)),
      switchMap(([{ password }, activationToken]) => {
        if (!activationToken) {
          return of(
            setError({
              error: this.translate.instant(
                'auth.passwordRecovery.errors.missingRecoverySession'
              ),
            })
          );
        }
        return this.authService
          .completePasswordRecovery({ activationToken, password })
          .pipe(
            map((res: CompletePasswordRecoveryResponse) => {
              const data = res?.data;
              const message = data?.message ?? res?.message ?? null;
              // Only an explicit SUCCESS changed the password; anything else
              // is a refusal the operator must see (F-045).
              if (data?.status?.toUpperCase?.() !== 'SUCCESS') {
                return setError({
                  error:
                    message ??
                    this.translate.instant(
                      'auth.passwordRecovery.errors.generic'
                    ),
                });
              }
              return setPasswordSuccess({
                message,
                resultStatus: data.status,
              });
            }),
            catchError((err: any) =>
              of(setPasswordFailure({ error: this.normalizeApiError(err) }))
            )
          );
      })
    )
  );

  setPasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setPasswordSuccess),
        map(() => {
          this.router.navigate(['/auth/password-recovery/restriction-info']);
        })
      ),
    { dispatch: false }
  );

  /**
   * The flow's own refusals (guard clauses, a non-SUCCESS completion) send no
   * request, or one the interceptor toasted as a success, and no recovery
   * screen renders `state.error` — so they are toasted here (F-043).
   */
  refusalToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setError),
        tap(({ error }) => {
          if (error) this.toast.error(error);
        })
      ),
    { dispatch: false }
  );

  complete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(complete),
        map(() => {
          this.router.navigate(['/auth/login']);
        })
      ),
    { dispatch: false }
  );

  private normalizeApiError(err: unknown): string {
    return extractErrorMessage(
      err,
      this.translate.instant('auth.passwordRecovery.errors.generic')
    );
  }
}
